<template>
  <MobilePage ref="pageRef" :locked="route.name === 'pwa-contact-detail'">
    <div class="p-4 space-y-3" :class="{ invisible: route.name === 'pwa-contact-detail' }">
      <form class="space-y-2 pb-1" @submit.prevent="search">
        <div class="grid grid-cols-2 gap-2">
          <div v-for="field in nameFields" :key="field.key" class="relative">
            <input
              v-model="filters[field.key]"
              type="search"
              :placeholder="field.label"
              class="w-full appearance-none bg-white rounded-md shadow-md border-none py-2 pl-3 pr-9 leading-5 text-xs text-gray-900 focus:ring-0"
            />
            <button v-if="filters[field.key]" type="button" class="absolute inset-y-0 right-0 flex items-center px-3" @click="filters[field.key] = ''">
              <XMarkIcon class="h-4 w-4 text-gray-400" />
            </button>
          </div>
        </div>
        <div class="relative">
          <input
            v-model="filters.companyName"
            type="search"
            placeholder="Company name"
            class="w-full appearance-none bg-white rounded-md shadow-md border-none py-2 pl-3 pr-9 leading-5 text-xs text-gray-900 focus:ring-0"
          />
          <button v-if="filters.companyName" type="button" class="absolute inset-y-0 right-0 flex items-center px-3" @click="filters.companyName = ''">
            <XMarkIcon class="h-4 w-4 text-gray-400" />
          </button>
        </div>
        <BaseComboBox v-model="filters.campaign" :options="campaignOptions" placeholder="Campaign" singleValue nullable clearable roundedClass="rounded-md" :page-size="20" />
        <div class="flex items-center justify-end gap-4">
          <button type="button" class="text-sm text-accent-primary" @click="clearFilters">Clear</button>
          <button type="submit" class="px-4 py-2 rounded-md bg-dark-primary text-white">Search</button>
        </div>
      </form>

      <div v-if="!isOnline && contacts.length === 0" class="text-center text-gray-500 py-8">Connect to the internet to load contacts</div>
      <div v-else-if="loadFailed" class="text-center text-gray-500 py-8 space-y-3">
        <div>Couldn't load contacts</div>
        <button class="px-4 py-2 rounded-md bg-dark-primary text-white" @click="loadContacts(page)">Retry</button>
      </div>
      <template v-else-if="isLoading">
        <MobileContactCardSkeleton v-for="n in 5" :key="n" />
      </template>
      <div v-else-if="contacts.length === 0" class="text-center text-gray-500 py-8">No contacts</div>
      <template v-else>
        <MobileContactCard v-for="contact in contacts" :key="contact.id" :contact="contact" class="cursor-pointer" @click="openContact(contact)" />
      </template>

      <div v-if="numPages > 1" class="flex items-center justify-center gap-6 pt-1 text-sm">
        <button class="px-3 py-2 rounded-md bg-white shadow text-dark-primary disabled:opacity-40" :disabled="isLoading || page <= 1" @click="changePage(page - 1)">‹ Prev</button>
        <div class="text-center text-xs text-gray-500">
          <div>Page {{ page }} of {{ numPages }}</div>
          <div>{{ count }} contacts</div>
        </div>
        <button class="px-3 py-2 rounded-md bg-white shadow text-dark-primary disabled:opacity-40" :disabled="isLoading || page >= numPages" @click="changePage(page + 1)">Next ›</button>
      </div>
    </div>
    <router-view v-slot="{ Component }">
      <!-- Kept alive so switching tabs and back returns to the same detail and scroll position -->
      <div v-show="route.name === 'pwa-contact-detail'" class="fixed inset-0 z-40 bg-gray-100">
        <keep-alive :max="1" :key="detailCacheKey">
          <component v-if="Component && route.name === 'pwa-contact-detail'" :is="Component" :key="route.params.contactId" />
        </keep-alive>
      </div>
    </router-view>
  </MobilePage>
</template>

<script setup>
import { ref, reactive, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { useOnline } from '@vueuse/core';
import { getContacts } from '@/services/Contact';
import { getMinimalActiveCampaigns } from '@/services/Campaign';
import { XMarkIcon } from '@heroicons/vue/20/solid';
import BaseComboBox from '@/components/BaseComboBox.vue';
import MobilePage from '@/components/mobile/base/MobilePage.vue';
import MobileContactCard from '@/components/mobile/contact/MobileContactCard.vue';
import MobileContactCardSkeleton from '@/components/mobile/contact/MobileContactCardSkeleton.vue';

const PAGE_SIZE = 25

const store = useStore()
const router = useRouter()
const route = useRoute()
const pageRef = ref(null)

// Closing the detail back to this list drops the cached detail, so reopening a contact starts fresh
const detailCacheKey = ref(0)
watch(() => route.name, (name, previousName) => {
  if (name === 'pwa-contacts' && previousName === 'pwa-contact-detail') detailCacheKey.value++
})
const token = store.getters['auth/showToken']
const isOnline = useOnline()

const contacts = ref([])
const page = ref(1)
const numPages = ref(0)
const count = ref(0)
const isLoading = ref(false)
const loadFailed = ref(false)

const nameFields = [
  { key: 'firstName', label: 'First name' },
  { key: 'lastName', label: 'Last name' },
]
const emptyFilters = () => ({ firstName: '', lastName: '', companyName: '', campaign: null })
const filters = reactive(emptyFilters())
const campaignOptions = ref([])

let latestRequestId = 0
// Filters as of the last Search click, so Prev/Next page the same results even if the inputs were edited since
let searchedParams = new URLSearchParams()

function buildParams() {
  const params = new URLSearchParams({ is_active: 'True', page_size: PAGE_SIZE })
  if (filters.firstName.trim()) params.append('first_name', filters.firstName.trim())
  if (filters.lastName.trim()) params.append('last_name', filters.lastName.trim())
  if (filters.companyName.trim()) params.append('company_name', filters.companyName.trim())
  if (filters.campaign) params.append('company_campaign', filters.campaign)
  return params
}

function search() {
  searchedParams = buildParams()
  changePage(1)
}

function clearFilters() {
  Object.assign(filters, emptyFilters())
}

async function loadCampaignOptions() {
  try {
    const campaigns = await getMinimalActiveCampaigns(token)
    campaignOptions.value = (campaigns || []).map(c => ({ value: c.id, label: c.campaign_name }))
  } catch (error) {
    console.log(error)
  }
}

async function loadContacts(pageNumber) {
  if (!isOnline.value) return
  const requestId = ++latestRequestId
  isLoading.value = true
  loadFailed.value = false
  const params = new URLSearchParams(searchedParams)
  params.append('page', pageNumber)
  const result = await getContacts({ token, endpoint: `/?${params}` })

  if (requestId !== latestRequestId) return
  isLoading.value = false
  if (!result) {
    loadFailed.value = true
    return
  }
  contacts.value = result.contacts
  page.value = result.page
  numPages.value = result.num_pages
  count.value = result.count
}

function openContact(contact) {
  router.push({
    name: 'pwa-contact-detail',
    params: { contactId: contact.id },
    state: { contact: JSON.parse(JSON.stringify(contact)) },
  })
}

function changePage(pageNumber) {
  if (pageRef.value) pageRef.value.scrollToTop()
  loadContacts(pageNumber)
}

watch(isOnline, online => {
  if (!online) return
  if (campaignOptions.value.length === 0) loadCampaignOptions()
  if (contacts.value.length === 0) loadContacts(page.value)
})

loadCampaignOptions()
search()
</script>
